import { Sprite, type Container, type Texture } from 'pixi.js';

/** Far outside any stage: a parked sprite is clipped before it costs a pixel. */
export const PARKED_AT = -1e5;

/**
 * Reusable sprites for short-lived effects (trail dots, sparks, flashes).
 *
 * A released sprite stays a child of its layer, parked off-stage at zero alpha, instead of
 * being removed or hidden: in Pixi v8 adding, removing or toggling `visible` on a child
 * makes the whole render group rebuild its draw list, which per effect per frame is the
 * cost this pool exists to avoid. Moving and fading a sprite only touches its own vertices.
 */
export class SpritePool {
  private readonly free: Sprite[] = [];

  constructor(private readonly layer: Container) {}

  acquire(texture: Texture, anchorX = 0.5, anchorY = 0.5): Sprite {
    const s = this.free.pop() ?? this.create();
    s.texture = texture;
    s.anchor.set(anchorX, anchorY);
    s.scale.set(1);
    s.rotation = 0;
    s.tint = 0xffffff;
    s.alpha = 1;
    return s;
  }

  release(s: Sprite): void {
    s.alpha = 0;
    s.position.set(PARKED_AT, PARKED_AT);
    this.free.push(s);
  }

  private create(): Sprite {
    const s = new Sprite();
    this.layer.addChild(s);
    return s;
  }
}
